import { ActionButton } from '@/components/record-sheet/ActionButton'
import { useRecordSheet } from '@/components/record-sheet/context'
import { btnSecondary } from '@/components/record-sheet/styles'
import { type SheetAction, type SheetTab, fetchRecordTab } from '@/lib/sheet'
import { cn } from '@/lib/utils'
import { useQuery } from '@tanstack/react-query'
import { AlertCircle, ChevronDown, ChevronLeft, ChevronRight, Play } from 'lucide-react'
import { useState } from 'react'

const PAGE_SIZE = 5

interface Props {
  resourceKey: string
  id: string | number
  tab: SheetTab
  actions: SheetAction[]
  onAction: (a: SheetAction) => void
}

/**
 * One related list (menus, reviews, gallery…). Collapsed by default and only
 * fetched when opened, five rows per page, so big businesses stay fast.
 */
export function RelatedSection({ resourceKey, id, tab, actions, onAction }: Props) {
  const [open, setOpen] = useState(false)

  return (
    <section className="overflow-hidden rounded-[36px] border border-neutral-200 bg-white dark:border-neutral-800 dark:bg-neutral-900">
      <div className="flex items-center gap-3 px-5 py-4">
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="flex min-w-0 flex-1 items-center gap-3 text-left"
        >
          <ChevronDown
            className={cn(
              'size-4 shrink-0 text-neutral-400 transition-transform',
              !open && '-rotate-90'
            )}
          />
          <span className="truncate text-base font-bold text-neutral-900 dark:text-white">
            {tab.label}
          </span>
          <span className="rounded-full bg-neutral-100 px-2.5 py-0.5 text-xs font-semibold tabular-nums text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">
            {tab.count}
          </span>
        </button>
        {actions.map((a) => (
          <ActionButton key={a.id} action={a} onClick={() => onAction(a)} />
        ))}
      </div>
      {open && <RelatedList resourceKey={resourceKey} id={id} tab={tab} />}
    </section>
  )
}

function RelatedList({ resourceKey, id, tab }: { resourceKey: string; id: string | number; tab: SheetTab }) {
  const { openRecord } = useRecordSheet()
  const [cursors, setCursors] = useState<string[]>([])
  const cursor = cursors[cursors.length - 1]
  const page = useQuery({
    queryKey: ['record-tab', resourceKey, String(id), tab.id, cursor ?? ''],
    queryFn: () => fetchRecordTab(resourceKey, id, tab.id, cursor, PAGE_SIZE),
    placeholderData: (prev) => prev
  })

  if (page.isLoading) {
    return (
      <div className="space-y-2 border-t border-neutral-200 p-5 dark:border-neutral-800">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3">
            <div className="size-12 animate-pulse rounded-full bg-neutral-200 dark:bg-neutral-800" />
            <div className="h-3 w-1/2 animate-pulse rounded-full bg-neutral-200 dark:bg-neutral-800" />
          </div>
        ))}
      </div>
    )
  }

  if (page.error || !page.data) {
    return (
      <div className="flex items-center justify-between gap-3 border-t border-neutral-200 px-5 py-4 dark:border-neutral-800">
        <span className="inline-flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
          <AlertCircle className="size-4" /> Couldn't load {tab.label.toLowerCase()}
        </span>
        <button type="button" className={btnSecondary} onClick={() => page.refetch()}>
          Retry
        </button>
      </div>
    )
  }

  const items = page.data.results
  const pageNumber = cursors.length + 1
  const totalPages = Math.max(1, Math.ceil(tab.count / PAGE_SIZE))
  const nextCursor = page.data.next
    ? (new URL(page.data.next).searchParams.get('cursor') ?? undefined)
    : undefined

  return (
    <div className="border-t border-neutral-200 dark:border-neutral-800">
      {items.length === 0 ? (
        <p className="px-5 py-6 text-center text-sm text-neutral-500 dark:text-neutral-400">
          Nothing here yet.
        </p>
      ) : (
        <ul className="divide-y divide-neutral-200 dark:divide-neutral-800">
          {items.map((item) => (
            <li key={String(item.id)}>
              <button
                type="button"
                onClick={() => openRecord(tab.resource, item.id)}
                className="flex w-full items-center gap-4 px-5 py-3 text-left transition-colors hover:bg-neutral-50 dark:hover:bg-neutral-800/60"
              >
                {item.image ? (
                  <span className="relative size-14 shrink-0 overflow-hidden rounded-full bg-neutral-100 dark:bg-neutral-800">
                    {item.image.video ? (
                      <>
                        <video src={item.image.url} muted playsInline className="size-full object-cover" />
                        <Play className="absolute inset-0 m-auto size-4 text-white" />
                      </>
                    ) : (
                      <img src={item.image.url} alt="" loading="lazy" className="size-full object-cover" />
                    )}
                  </span>
                ) : null}
                <span className="min-w-0 flex-1">
                  <span className="line-clamp-2 text-sm font-semibold text-neutral-900 dark:text-white">
                    {item.title}
                  </span>
                  {(item.meta.length > 0 || item.date) && (
                    <span className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-neutral-500 dark:text-neutral-400">
                      {item.meta.map((m) => (
                        <span
                          key={m}
                          className="rounded-full bg-neutral-100 px-2 py-0.5 font-semibold text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300"
                        >
                          {m}
                        </span>
                      ))}
                      {item.date && <span>{item.date}</span>}
                    </span>
                  )}
                </span>
                <ChevronRight className="size-4 shrink-0 text-neutral-400" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between gap-3 border-t border-neutral-200 px-5 py-3 dark:border-neutral-800">
          <span className="text-xs tabular-nums text-neutral-500 dark:text-neutral-400">
            Page {pageNumber} of {totalPages}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              className={btnSecondary}
              disabled={cursors.length === 0 || page.isFetching}
              onClick={() => setCursors((c) => c.slice(0, -1))}
            >
              <ChevronLeft className="size-4" /> Previous
            </button>
            <button
              type="button"
              className={btnSecondary}
              disabled={!nextCursor || page.isFetching}
              onClick={() => nextCursor && setCursors((c) => [...c, nextCursor])}
            >
              Next <ChevronRight className="size-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
